import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import translations from "../assets/locals/translations";
import { setShowMessage } from "../redux/slices/notificationSlice"; // Assuming correct path
import {
  Card,
  Button,
  Grid,
  Container,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Divider,
  Avatar,
  Stack, // Use Stack for better layout control
  List, // For better structured details
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress, // For loading state
  Box, // Generic container
  Alert, // For displaying form-level errors if needed
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined, // Icon for Email
  PhoneOutlined, // Icon for Phone
  LockOutlined, // Icon for Password fields
  LockResetOutlined,
  VpnKeyOutlined,
} from "@mui/icons-material";

// Yup Validation Schema (remains the same)
const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string().required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Confirm password is required"),
});

const UserProfile = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profileData.profile); // Assuming correct path
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    avatarUrl: "", // Add a placeholder for avatar URL
  });
  const [formError, setFormError] = useState(""); // State for API errors

  useEffect(() => {
    if (profile?.currentUserData?.result) {
      const { first_name, last_name, email, phone_number, profile_picture } =
        profile.currentUserData.result;
      setUserData({
        firstName: first_name || "User",
        lastName: last_name || "",
        email: email || "N/A",
        phoneNumber: phone_number || "N/A",
        avatarUrl: profile_picture || "", // Use actual profile picture URL if available
      });
    }
  }, [profile]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    setFormError(""); // Clear previous errors
    try {
      const url = `${process.env.REACT_APP_NODE_URL}/api/users/changepassword`;
      const config = { headers: { token: localStorage.getItem("rbacToken") } };
      const response = await axios.post(
        url,
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          email: userData.email, // Make sure email is passed correctly
        },
        config
      );

      if (response.data.isSuccess) {
        dispatch(
          setShowMessage({
            message: t.passwordChangedSuccess,
            severity: "success",
          })
        );
        resetForm();
      } else {
        // Handle specific API errors (e.g., incorrect current password)
        setFormError(response.data.message || t.failedToChangePassword);
        dispatch(
          setShowMessage({
            message: response.data.message || t.failedToChangePassword,
            severity: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error changing password:", error);
      const message =
        error.response?.data?.message || "An unexpected error occurred.";
      setFormError(message);
      dispatch(setShowMessage({ message, severity: "error" }));
    } finally {
      setSubmitting(false); // Ensure submission state is reset
    }
  };

  const getInitials = (firstName = "", lastName = "") => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={4} justifyContent="center">
        {/* Personal Details Section */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: "medium", mb: 3 }}
            >
              {t.personalInformation}
            </Typography>
            <Stack direction="column" alignItems="center" spacing={2} mb={3}>
              <Avatar
                alt={`${userData.firstName} ${userData.lastName}`}
                src={userData.avatarUrl || "/static/images/avatar/fallback.jpg"} // Use actual or fallback
                sx={{
                  width: 100,
                  height: 100,
                  fontSize: "2.5rem",
                  bgcolor: "primary.main",
                }} // Larger avatar
              >
                {!userData.avatarUrl &&
                  getInitials(userData.firstName, userData.lastName)}
              </Avatar>
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "medium" }}
              >
                {`${userData.firstName} ${userData.lastName}`}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <EmailOutlined color="action" />
                </ListItemIcon>
                <ListItemText primary={t.emailAddress} secondary={userData.email} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PhoneOutlined color="action" />
                </ListItemIcon>
                <ListItemText primary={t.mobileNumber} secondary={userData.phoneNumber} />
              </ListItem>
              {/* Add more details here if needed */}
            </List>
            {/* Optional: Add an Edit Profile Button */}
            {/* <Button variant="outlined" sx={{ mt: 'auto' }}>Edit Profile</Button> */}
          </Card>
        </Grid>

        {/* Password Change Section */}
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3, height: "100%" }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: "medium", mb: 3 }}
            >
              {t.changePassword}
            </Typography>
            <Formik
              initialValues={{
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form>
                  <Stack spacing={3}>
                    {" "}
                    {/* Use Stack for form spacing */}
                    {formError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {formError}
                      </Alert>
                    )}
                    <Field
                      name="currentPassword"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      label={t.currentPasswordLabel}
                      type={showCurrentPassword ? "text" : "password"}
                      error={
                        touched.currentPassword &&
                        Boolean(errors.currentPassword)
                      }
                      helperText={
                        touched.currentPassword && errors.currentPassword
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyOutlined />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              edge="end"
                              aria-label="toggle current password visibility"
                            >
                              {showCurrentPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      name="newPassword"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      label={t.newPasswordLabel}
                      type={showPasswordNew ? "text" : "password"}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowPasswordNew(!showPasswordNew)
                              }
                              edge="end"
                              aria-label="toggle new password visibility"
                            >
                              {showPasswordNew ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      name="confirmPassword"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      label={t.confirmNewPasswordLabel}
                      type={showPasswordConfirm ? "text" : "password"}
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockResetOutlined />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowPasswordConfirm(!showPasswordConfirm)
                              }
                              edge="end"
                              aria-label="toggle confirm password visibility"
                            >
                              {showPasswordConfirm ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ position: "relative", mt: 2 }}>
                      {" "}
                      {/* Wrapper for button and progress */}
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={isSubmitting}
                        size="large" // Make button slightly larger
                        sx={{ py: 1.5 }} // Adjust padding
                      >
                        {isSubmitting ? t.changing : t.updatePassword}
                      </Button>
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: "primary.main",
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      )}
                    </Box>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserProfile;
